/**
 * User Menu Component
 * 
 * Shows user avatar/email when logged in, or sign in button when not.
 */

import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCredits } from '../contexts/CreditsContext';
import { useAdmin } from '../hooks/useAdmin';
import { CreditsDisplay } from './CreditsDisplay';
import AuthModal from './AuthModal';
import RedeemCodeModal from './ui/RedeemCodeModal';
import BuyCreditsModal from './BuyCreditsModal';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import RedeemIcon from '@mui/icons-material/Redeem';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import './UserMenu.css';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import EditIcon from '@mui/icons-material/Edit';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LockResetIcon from '@mui/icons-material/LockReset';
import LockIcon from '@mui/icons-material/Lock';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import PublicIcon from '@mui/icons-material/Public';
import GridOnIcon from '@mui/icons-material/GridOn';
import { useSnapGrid, setSnapToGridEnabled, setSnapGridSize, MIN_SNAP_GRID_SIZE, MAX_SNAP_GRID_SIZE } from '../utils/canvasSnapGrid';
import ContactModal from './ui/ContactModal';
import ChangePasswordModal from './ui/ChangePasswordModal';

interface UserMenuProps {
  onSaveAsTemplate?: () => void;
  onUpdateTemplate?: () => void;
  hasLoadedTemplate?: boolean;
  creditsTooltipMode?: 'history' | 'buy-only';
}

export const UserMenu: React.FC<UserMenuProps> = ({ onSaveAsTemplate, onUpdateTemplate, hasLoadedTemplate, creditsTooltipMode = 'history' }) => {
  const navigate = useNavigate();
  const { user, loading, signOut } = useAuth();
  const { subscription, tier } = useCredits();
  const { isAdmin } = useAdmin();
  // Agents/MCP (IRIS + OpenClaw) is a Pro/Corporate feature.
  const isProTier = tier === 'pro' || tier === 'corporate';
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const avatarRef = useRef<HTMLButtonElement>(null);
  const { enabled: snapToGrid, size: snapGridSize } = useSnapGrid();

  if (loading) {
    return <div className="user-menu-loading">...</div>;
  }

  if (!user) {
    return (
      <>
        <button
          className="user-menu-signin"
          onClick={() => setShowAuthModal(true)}
        >
          Sign In
        </button>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      </>
    );
  }

  const initials = user.email
    ? user.email.substring(0, 2).toUpperCase()
    : '??';

  const handleSignOut = async () => {
    await signOut();
    setShowDropdown(false);
  };

  const handleAvatarClick = () => {
    if (avatarRef.current) {
      const rect = avatarRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        left: rect.right - 200 // Align right edge with avatar
      });
    }
    setShowDropdown(!showDropdown);
  };

  return (
    <div className="user-menu">
      <CreditsDisplay compact showLabel={false} tooltipMode={creditsTooltipMode} onBuyCredits={() => setShowBuyCreditsModal(true)} />
      <button
        ref={avatarRef}
        className="user-menu-avatar"
        onClick={handleAvatarClick}
        title={user.email || 'User'}
      >
        {user.user_metadata?.avatar_url ? (
          <img
            src={user.user_metadata.avatar_url}
            alt="Avatar"
            className="user-menu-avatar-img"
          />
        ) : (
          <span className="user-menu-initials">{initials}</span>
        )}
      </button>

      {showDropdown && createPortal(
        <>
          <div
            className="user-menu-backdrop"
            onClick={() => setShowDropdown(false)}
          />
          <div 
            className="user-menu-dropdown"
            style={{
              position: 'fixed',
              top: `${dropdownPos.top}px`,
              left: `${dropdownPos.left}px`,
              zIndex: 10001
            }}
          >
            <div className="user-menu-email">{user.email}</div>
            {subscription && (
              <div className="user-menu-subscription">
                <WorkspacePremiumIcon fontSize="small" className="subscription-icon" />
                <span className="subscription-plan">{subscription.planName}</span>
                <span className="subscription-badge">{subscription.billingCycle === 'annual' ? 'Annual' : 'Monthly'}</span>
              </div>
            )}
            <hr className="user-menu-divider" />
            <button 
              className="user-menu-item upgrade-item" 
              onClick={() => {
                navigate('/pricing');
                setShowDropdown(false);
              }}
            >
              <WorkspacePremiumIcon fontSize="small" />
              {subscription ? 'Manage Plan' : 'Upgrade Plan'}
            </button>
            <button 
              className="user-menu-item buy-credits-item" 
              onClick={() => {
                setShowBuyCreditsModal(true);
                setShowDropdown(false);
              }}
            >
              <ShoppingCartIcon fontSize="small" />
              Buy Credits
            </button>
            <button
              className="user-menu-item"
              onClick={() => {
                navigate('/member-credits');
                setShowDropdown(false);
              }}
            >
              <AccountCircleIcon fontSize="small" />
              Member & Credits
            </button>
            <button
              className="user-menu-item"
              onClick={() => {
                navigate('/publishes');
                setShowDropdown(false);
              }}
            >
              <PublicIcon fontSize="small" />
              My Publishes
            </button>
            <button
              className="user-menu-item redeem-item"
              onClick={() => {
                setShowRedeemModal(true);
                setShowDropdown(false);
              }}
            >
              <RedeemIcon fontSize="small" />
              Redeem Code
            </button>
            <button
              className="user-menu-item"
              onClick={() => {
                navigate('/settings/keys');
                setShowDropdown(false);
              }}
            >
              <VpnKeyIcon fontSize="small" />
              API Keys
            </button>
            <button
              className="user-menu-item"
              onClick={() => {
                if (isProTier) {
                  navigate('/settings/agents');
                } else {
                  navigate('/pricing');
                }
                setShowDropdown(false);
              }}
              title={isProTier ? undefined : 'Agents are a Pro feature — upgrade to unlock'}
            >
              <SmartToyIcon fontSize="small" />
              Agent Settings
              {!isProTier && (
                <span className="user-menu-pro-badge">
                  <LockIcon sx={{ fontSize: 12 }} />
                  Pro
                </span>
              )}
            </button>
            <button
              className="user-menu-item"
              onClick={() => setSnapToGridEnabled(!snapToGrid)}
              title="Snap nodes to a 12px grid while dragging"
            >
              <GridOnIcon fontSize="small" />
              Snap to Grid
              <span className={`user-menu-switch ${snapToGrid ? 'on' : ''}`} aria-hidden="true">
                <span className="user-menu-switch-thumb" />
              </span>
            </button>
            {snapToGrid && (
              <div className="user-menu-slider-row" onClick={(e) => e.stopPropagation()}>
                <input
                  type="range"
                  min={MIN_SNAP_GRID_SIZE}
                  max={MAX_SNAP_GRID_SIZE}
                  step={16}
                  value={snapGridSize}
                  onChange={(e) => setSnapGridSize(Number(e.target.value))}
                  aria-label="Grid size in pixels"
                />
                <span className="user-menu-slider-value">{snapGridSize}px</span>
              </div>
            )}
            {isAdmin && (
              <>
                <hr className="user-menu-divider" />
                {onSaveAsTemplate && (
                  <button 
                    className="user-menu-item admin-item" 
                    onClick={() => {
                      onSaveAsTemplate();
                      setShowDropdown(false);
                    }}
                  >
                    <BookmarkAddIcon fontSize="small" />
                    Save as Template
                  </button>
                )}
                {hasLoadedTemplate && onUpdateTemplate && (
                  <button 
                    className="user-menu-item admin-item" 
                    onClick={() => {
                      onUpdateTemplate();
                      setShowDropdown(false);
                    }}
                  >
                    <EditIcon fontSize="small" />
                    Update Template
                  </button>
                )}
                <button 
                  className="user-menu-item admin-item" 
                  onClick={() => {
                    navigate('/admin');
                    setShowDropdown(false);
                  }}
                >
                  <CardGiftcardIcon fontSize="small" />
                  Admin Dashboard
                </button>
              </>
            )}
            <hr className="user-menu-divider" />
            <button
              className="user-menu-item"
              onClick={() => {
                setShowChangePasswordModal(true);
                setShowDropdown(false);
              }}
            >
              <LockResetIcon fontSize="small" />
              Change Password
            </button>
            <button
              className="user-menu-item"
              onClick={() => {
                setShowContactModal(true);
                setShowDropdown(false);
              }}
            >
              <HelpOutlineIcon fontSize="small" />
              Help & Contact
            </button>
            <button className="user-menu-item" onClick={handleSignOut}>
              Sign Out
            </button>
          </div>
        </>,
        document.body
      )}

      {/* Redeem Code Modal */}
      <RedeemCodeModal 
        isOpen={showRedeemModal}
        onClose={() => setShowRedeemModal(false)}
      />

      {/* Buy Credits Modal */}
      <BuyCreditsModal
        isOpen={showBuyCreditsModal}
        onClose={() => setShowBuyCreditsModal(false)}
      />

      {/* Contact Modal */}
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />

    </div>
  );
};

export default UserMenu;
